// Mock data for Ravi Prakash Speaker Website

export const speakingTopics = [
  {
    id: 1,
    title: "Leadership Under Pressure",
    shortDescription: "How to lead with steadiness, courage, and humanity when expectations are high and certainty is low.",
    fullDescription: "How leaders respond when the pressure is real, the answers are incomplete, and people are looking to them for steadiness.",
    takeaways: [
      "Lead with clarity under stress",
      "Communicate with calm and courage",
      "Make decisions without pretending certainty",
      "Stay human while carrying responsibility"
    ],
    bestFor: "Corporate leaders, managers, leadership teams, executive audiences"
  },
  {
    id: 2,
    title: "Resilience During Change",
    shortDescription: "How people and teams can respond to setbacks, transitions, and disruption without losing themselves.",
    fullDescription: "A keynote on setbacks, uncertainty, adaptation, and the mindset required to keep moving when the path changes.",
    takeaways: [
      "Respond to setbacks without collapsing inward",
      "Rebuild confidence after disruption",
      "Find momentum in uncertain seasons",
      "Grow without romanticizing struggle"
    ],
    bestFor: "Organizations in transition, conferences, universities, graduates, emerging leaders"
  },
  {
    id: 3,
    title: "Reinvention and Identity",
    shortDescription: "A powerful keynote for audiences navigating growth, transition, belonging, reinvention, or the pressure to prove themselves.",
    fullDescription: "A talk for audiences navigating growth, migration, identity shifts, career transitions, or the pressure to prove themselves in unfamiliar spaces.",
    takeaways: [
      "Rethink identity during transition",
      "Release outdated definitions of success",
      "Build confidence from within",
      "Move forward without waiting to feel fully ready"
    ],
    bestFor: "Young professionals, graduates, immigrant audiences, leadership development events"
  },
  {
    id: 4,
    title: "Staying Human in the Age of AI",
    shortDescription: "A timely talk on leadership, meaning, and human responsibility in a fast-changing AI-driven world.",
    fullDescription: "A keynote on leadership, technology, judgment, and the responsibility to protect what is human in an AI-shaped world.",
    takeaways: [
      "Understand the human side of AI-driven change",
      "Think more clearly about leadership in disruption",
      "Build cultures that do not lose meaning in the chase for speed",
      "Ask better questions about progress, ethics, and human value"
    ],
    bestFor: "Executive teams, leadership events, innovation conferences, AI and future-of-work audiences"
  }
];

export const testimonials = [
  {
    id: 1,
    quote: "A great gift for graduates, anyone who feels stuck, and fans of Randy Pausch's The Last Lecture. This inspiring and insightful book rises above the level of most self-published memoirs and is so engaging that I read it in one sitting. It shows a great example of someone moving past setbacks with integrity and having the confidence to reject situations that do not align with personal values.",
    author: "Tori Cavitt",
    title: "Book Reviewer",
    organization: "Amazon"
  },
  {
    id: 2,
    quote: "This book is a deeply inspiring story of overcoming obstacles while staying true to one's core values. It traces his path from a modest upbringing in India to his rise as a corporate leader in the U.S., demonstrating the power of persistence, hard work, and moral integrity. What makes his journey remarkable is his unwavering commitment to ethical principles, even when facing tough situations and challenging decisions.",
    author: "Ritesh Parashar",
    title: "Reader",
    organization: "Amazon Reviews"
  },
  {
    id: 3,
    quote: "Unforgettable story with practical wisdom. Ravi Prakash's book tells an unforgettable story of how hardship and adversity can temper a human being like true steel and provide wisdom and insight on the other side. Read this book for the story, but stay for the advice on how to live a more beautiful and productive life.",
    author: "Nick Morgan, PhD",
    title: "Communications Coach, Former Harvard Kennedy School Fellow",
    organization: "Author of Working the Room and Power Cues"
  },
  {
    id: 4,
    quote: "These electrifying words from former cricket prodigy turned international speaker Ravi Prakash left the room stunned at last Monday's career transition event. Ravi delivered a masterclass in turning professional setbacks into extraordinary comebacks.",
    author: "Carlos Alejandro Gorricho",
    title: "AI/ML & Finance Executive",
    organization: "Career Transition Event"
  }
];

export const audienceTypes = [
  "Corporate leaders and people managers",
  "Leadership retreat and off-site audiences",
  "Conference and summit attendees",
  "Young professionals and emerging leaders",
  "University and graduation audiences",
  "Organizations navigating the human side of change"
];

export const eventFormats = [
  "Keynote speaking",
  "Fireside chats",
  "Panel discussions",
  "Commencement and graduation talks",
  "Conference sessions",
  "Executive off-sites",
  "Author talks",
  "Book signing events"
];

export const whyBookRavi = [
  {
    title: "Lived experience that gives the message weight",
    description: "From $20 to global executive, Ravi's journey provides authentic insight."
  },
  {
    title: "Executive leadership that gives it credibility",
    description: "COO of CompanyInsights.AI with teams led in 15+ countries."
  },
  {
    title: "Stage presence that keeps people engaged",
    description: "TEDx speaker with proven ability to connect and hold attention."
  },
  {
    title: "Language that is direct, human, and memorable",
    description: "No guru speak. Just grounded wisdom that sticks."
  }
];

export const bookInfo = {
  title: "Safar: An Immigrant's Journey of Life & Leadership",
  description: "Part memoir, part leadership reflection, Safar is for people who have had to rebuild, adapt, push forward, and grow through uncertainty. It is a book about resilience, reinvention, leadership, and the inner work required to keep going when the path is hard.",
  idealFor: [
    "Professionals in transition",
    "Immigrants and first-generation strivers",
    "Graduates entering uncertainty",
    "Leaders seeking perspective and courage"
  ],
  amazonLink: "https://www.amazon.com/Safar-Immigrants-Journey-Life-Leadership/dp/B0DGZPXF49/ref=sr_1_1?crid=2OICEIZ4TE3U9&dib=eyJ2IjoiMSJ9.SQhmP_bOTn62gzyoWj-CJQ.iCF7GgAqHHyPC3Lc88Qb9mi4Pu8ZDELn1FvOIQ5wYDk&dib_tag=se&keywords=safar-an+immigrant+journey+of+life+and+leadership&qid=1775770540&sprefix=%2Caps%2C248&sr=8-1"
};

export const mediaKitContent = {
  bio: "Ravi Prakash is a keynote speaker, bestselling author of Safar: An Immigrant's Journey of Life & Leadership, and COO of CompanyInsights.AI. He speaks to corporate leaders, emerging professionals, graduates, and organizations navigating pressure, change, and identity. Having come to the United States with $20 and built a global career across industries and countries, Ravi brings both lived experience and executive perspective to the stage. His talks focus on leadership under pressure, resilience during change, reinvention and identity, and staying human in the age of AI.",
  journey: "I came to the U.S. with $20, led teams in 15+ countries, and delivered 60+ global projects. Before becoming an executive, I was a cricket prodigy whose career ended too soon. That loss taught me about resilience, reinvention, and the courage to start over.",
  philosophy: "No hype. Just real stories from real life to move people to action.",
  usps: [
    { title: "Proven Impact", description: "Measurable results from audiences worldwide" },
    { title: "Authentic", description: "Real stories, not manufactured inspiration" },
    { title: "Global Experience", description: "Led teams across 15+ countries" },
    { title: "Engaging & Actionable", description: "Audiences leave with tools they can use" }
  ],
  contact: {
    email: "",
    phone: "",
    website: "www.journeywithravi.com",
    linkedin: "https://www.linkedin.com/in/journeywithravi/"
  }
};
